import { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  FileText,
  Search,
  X,
  Monitor,
  MoreHorizontal,
  Eye,
  Copy,
  Ban,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  ShieldCheck,
  Handshake,
  Sparkles,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useContracts } from '@/hooks/api/use-contracts';
import { useTeamMembers } from '@/hooks/api/use-team';
import { usePermissions } from '@/hooks/api/use-permissions';
import { api } from '@/lib/api-client';
import { contractKeys } from '@/lib/query-keys';
import {
  ContractStatusBadge,
  ContractTypeBadge,
} from '@/components/contracts/contract-status-badge';
import { CancelContractModal } from '@/components/contracts/cancel-contract-modal';
import { DeleteContractModal } from '@/components/contracts/delete-contract-modal';
import { DuplicateContractModal } from '@/components/contracts/duplicate-contract-modal';

import type { ContractListItem } from '@/hooks/api/use-contracts';

export const Route = createFileRoute('/_authenticated/contracts/')({
  component: ContractsListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    status: typeof search.status === 'string' ? search.status : (undefined as string | undefined),
  }),
});

// --- Helpers ---

function formatDate(dateStr: string, language: string): string {
  return new Date(dateStr).toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const CANCELLABLE_STATUSES = new Set([
  'FORM_FILLING',
  'DRAFT',
  'GENERATION_FAILED',
  'CHANGES_REQUESTED',
]);

const PAGE_SIZES = [10, 25, 50];

// --- Creator Avatar ---

function CreatorAvatar({ name, email }: { name: string | null; email: string | null }) {
  const displayName = name || email || '?';
  const initials = name
    ? name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : (email?.[0] ?? '?').toUpperCase();

  return (
    <div className="flex items-center gap-2">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-medium text-primary">
        {initials}
      </div>
      <span className="truncate max-w-[120px]" title={displayName}>
        {displayName}
      </span>
    </div>
  );
}

// --- Skeleton ---

function PageSkeleton() {
  return (
    <div className="mx-auto hidden max-w-5xl space-y-6 lg:block">
      <div className="flex items-center justify-between">
        <div className="h-8 w-40 animate-pulse rounded-md bg-muted" />
        <div className="h-9 w-36 animate-pulse rounded-md bg-muted" />
      </div>
      <div className="flex gap-3">
        <div className="h-9 w-64 animate-pulse rounded-md bg-muted" />
        <div className="h-9 w-32 animate-pulse rounded-md bg-muted" />
        <div className="h-9 w-32 animate-pulse rounded-md bg-muted" />
      </div>
      <div className="h-80 animate-pulse rounded-xl border bg-card" />
    </div>
  );
}

// --- Empty State ---

function EmptyState({ onCreateClick, isLawyer }: { onCreateClick: () => void; isLawyer: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
      <div className="mb-6 flex size-16 items-center justify-center rounded-2xl bg-primary/10">
        <FileText className="size-8 text-primary" />
      </div>
      <h3 className="text-lg font-semibold">
        {t(isLawyer ? 'contractsList.lawyerEmptyTitle' : 'contractsList.emptyTitle')}
      </h3>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
        {t(isLawyer ? 'contractsList.lawyerEmptyDescription' : 'contractsList.emptyDescription')}
      </p>
      {!isLawyer && (
        <Button className="mt-8 cursor-pointer" size="lg" onClick={onCreateClick}>
          <Plus className="size-4" />
          {t('contractsList.emptyAction')}
        </Button>
      )}
    </div>
  );
}

// --- Create Contract Dialog ---

function CreateContractDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function handleSelect(type: 'NDA' | 'B2B') {
    onOpenChange(false);
    if (type === 'NDA') {
      void navigate({ to: '/contracts/new-nda' });
    } else {
      void navigate({ to: '/contracts/new-b2b' });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('contracts.selectType')}</DialogTitle>
          <DialogDescription>{t('contractsList.selectTypeDescription')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            className="flex items-center gap-3 rounded-lg border p-4 text-left transition-colors hover:border-primary/50 hover:bg-accent cursor-pointer"
            onClick={() => handleSelect('NDA')}
          >
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <ShieldCheck className="size-4.5 text-primary" />
            </div>
            <div>
              <span className="text-sm font-medium">{t('contracts.nda')}</span>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t('contracts.ndaDescription')}
              </p>
            </div>
          </button>
          <button
            type="button"
            className="flex items-center gap-3 rounded-lg border p-4 text-left transition-colors hover:border-primary/50 hover:bg-accent cursor-pointer"
            onClick={() => handleSelect('B2B')}
          >
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Handshake className="size-4.5 text-primary" />
            </div>
            <div>
              <span className="text-sm font-medium">{t('contracts.b2b')}</span>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t('contracts.b2bDescription')}
              </p>
            </div>
          </button>
        </div>
        {/* Coming soon teaser */}
        <div className="flex items-start gap-3 rounded-lg border border-dashed border-muted-foreground/20 p-4">
          <Sparkles className="mt-0.5 size-4 shrink-0 text-amber-500" />
          <div>
            <p className="text-sm font-medium text-muted-foreground">
              {t('contracts.moreTypesSoonTitle')}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground/60">
              {t('contracts.moreTypesSoonDescription')}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Filter Dropdown (custom, replaces native <select>) ---

function FilterDropdown({
  value,
  onChange,
  options,
  placeholder,
  showAllOption = true,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  showAllOption?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const selectedLabel = options.find((o) => o.value === value)?.label ?? placeholder;

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = (options.length + (showAllOption ? 1 : 0)) * 36 + 8;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const fitsBelow = spaceBelow >= menuHeight;
    setMenuPos({
      top: fitsBelow ? rect.bottom + 4 : rect.top - menuHeight - 4,
      left: rect.left,
      width: Math.max(rect.width, 180),
    });
  }, [open, options.length, showAllOption]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      )
        setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, close]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        className="flex h-10 items-center gap-2 rounded-md border border-input bg-transparent px-3 text-sm transition-colors hover:bg-accent cursor-pointer focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={value ? 'text-foreground' : 'text-muted-foreground'}>{selectedLabel}</span>
        <ChevronDown className="size-4 text-muted-foreground" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-50 max-h-60 overflow-y-auto rounded-md border bg-popover py-1 shadow-md"
            style={{ top: menuPos.top, left: menuPos.left, minWidth: menuPos.width }}
            role="listbox"
          >
            {showAllOption && (
              <button
                type="button"
                role="option"
                aria-selected={value === ''}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent cursor-pointer"
                onClick={() => {
                  onChange('');
                  close();
                }}
              >
                <Check className={`size-3.5 ${value === '' ? 'opacity-100' : 'opacity-0'}`} />
                {placeholder}
              </button>
            )}
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={value === opt.value}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent cursor-pointer"
                onClick={() => {
                  onChange(opt.value);
                  close();
                }}
              >
                <Check
                  className={`size-3.5 ${value === opt.value ? 'opacity-100' : 'opacity-0'}`}
                />
                {opt.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

// --- Filters ---

function Filters({
  search,
  onSearchChange,
  status,
  onStatusChange,
  contractType,
  onContractTypeChange,
  createdBy,
  onCreatedByChange,
  memberOptions,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  status: string;
  onStatusChange: (v: string) => void;
  contractType: string;
  onContractTypeChange: (v: string) => void;
  createdBy: string;
  onCreatedByChange: (v: string) => void;
  memberOptions: { value: string; label: string }[];
}) {
  const { t } = useTranslation();

  const statuses = [
    'FORM_FILLING',
    'GENERATING',
    'GENERATION_FAILED',
    'DRAFT_SELECTION',
    'DRAFT',
    'IN_REVIEW',
    'CHANGES_REQUESTED',
    'APPROVED',
    'REJECTED',
    'CANCELLED',
    'ARCHIVED',
  ];

  return (
    <div className="flex flex-wrap gap-3">
      {/* Search */}
      <div className="relative min-w-[200px] flex-1 sm:max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('contractsList.searchPlaceholder')}
          className="pl-9 pr-9"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
            aria-label={t('contractsList.clearSearch')}
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {/* Status filter */}
      <FilterDropdown
        value={status}
        onChange={onStatusChange}
        placeholder={t('contractsList.allStatuses')}
        options={statuses.map((s) => ({ value: s, label: t(`contractStatus.${s}`) }))}
      />

      {/* Type filter */}
      <FilterDropdown
        value={contractType}
        onChange={onContractTypeChange}
        placeholder={t('contractsList.allTypes')}
        options={[
          { value: 'NDA', label: t('contracts.nda') },
          { value: 'B2B', label: t('contracts.b2b') },
        ]}
      />

      {/* Created by filter */}
      {memberOptions.length > 0 && (
        <FilterDropdown
          value={createdBy}
          onChange={onCreatedByChange}
          placeholder={t('contractsList.allMembers')}
          options={memberOptions}
        />
      )}
    </div>
  );
}

// --- Pagination ---

function Pagination({
  page,
  totalPages,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  totalPages: number;
  pageSize: number;
  total: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (s: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-4 text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        <span>{t('contractsList.totalContracts', { count: total })}</span>
        <span className="text-muted-foreground/50">|</span>
        <FilterDropdown
          value={String(pageSize)}
          onChange={(v) => onPageSizeChange(Number(v))}
          placeholder=""
          showAllOption={false}
          options={PAGE_SIZES.map((size) => ({
            value: String(size),
            label: t('contractsList.perPage', { count: size }),
          }))}
        />
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon-sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="cursor-pointer"
          aria-label={t('contractsList.prevPage')}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="px-3 text-muted-foreground">
          {t('contractsList.pageOf', { page, totalPages: totalPages || 1 })}
        </span>
        <Button
          variant="outline"
          size="icon-sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="cursor-pointer"
          aria-label={t('contractsList.nextPage')}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

// --- Contract Row with Action Menu ---

function ContractRow({
  contract,
  language,
  onOpen,
  onDuplicate,
  onCancel,
  onDelete,
  isLawyer,
}: {
  contract: ContractListItem;
  language: string;
  onOpen: (id: string) => void;
  onDuplicate: (contract: ContractListItem) => void;
  onCancel: (contract: ContractListItem) => void;
  onDelete: (contract: ContractListItem) => void;
  isLawyer: boolean;
}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    triggerRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      left: rect.right - 192,
    });
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeMenu();
    }
    function handleScroll() {
      setMenuOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [menuOpen, closeMenu]);

  const canCancel = CANCELLABLE_STATUSES.has(contract.status);

  return (
    <tr
      className="border-b last:border-b-0 hover:bg-muted/50 transition-colors cursor-pointer"
      onClick={() => onOpen(contract.id)}
    >
      <td className="py-3 pr-4">
        <span className="font-medium">{contract.title || t('contractsList.untitled')}</span>
      </td>
      <td className="py-3 pr-4">
        <ContractTypeBadge type={contract.contractType} />
      </td>
      <td className="py-3 pr-4">
        <ContractStatusBadge status={contract.status} />
      </td>
      <td className="py-3 pr-4 text-sm">
        <CreatorAvatar name={contract.creatorName} email={contract.creatorEmail} />
      </td>
      <td className="py-3 pr-4 text-muted-foreground">
        {formatDate(contract.createdAt, language)}
      </td>
      <td className="py-3" onClick={(e) => e.stopPropagation()}>
        <div>
          <Button
            ref={triggerRef}
            variant="ghost"
            size="icon-sm"
            onClick={() => setMenuOpen(!menuOpen)}
            className="cursor-pointer"
            aria-label={t('contractsList.actionsLabel')}
            aria-haspopup="true"
            aria-expanded={menuOpen}
          >
            <MoreHorizontal className="size-4" />
          </Button>
          {menuOpen &&
            createPortal(
              <div
                ref={menuRef}
                className="fixed z-50 w-48 rounded-md border bg-popover py-1 shadow-md"
                style={{ top: menuPos.top, left: menuPos.left }}
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent cursor-pointer"
                  onClick={() => {
                    closeMenu();
                    onOpen(contract.id);
                  }}
                >
                  <Eye className="size-4" />
                  {t('contractsList.actionOpen')}
                </button>
                {!isLawyer && ['DRAFT', 'APPROVED', 'ARCHIVED'].includes(contract.status) && (
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent cursor-pointer"
                    onClick={() => {
                      closeMenu();
                      onDuplicate(contract);
                    }}
                  >
                    <Copy className="size-4" />
                    {t('contractsList.actionDuplicate')}
                  </button>
                )}
                {!isLawyer && canCancel && (
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-accent cursor-pointer"
                    onClick={() => {
                      closeMenu();
                      onCancel(contract);
                    }}
                  >
                    <Ban className="size-4" />
                    {t('contractsList.actionCancel')}
                  </button>
                )}
                {!isLawyer && contract.status === 'CANCELLED' && (
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-accent cursor-pointer"
                    onClick={() => {
                      closeMenu();
                      onDelete(contract);
                    }}
                  >
                    <Trash2 className="size-4" />
                    {t('contractsList.actionDelete')}
                  </button>
                )}
              </div>,
              document.body,
            )}
        </div>
      </td>
    </tr>
  );
}

// --- Mobile Message ---

function MobileMessage() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center lg:hidden">
      <div className="flex size-12 items-center justify-center rounded-full bg-muted">
        <Monitor className="size-6 text-muted-foreground" />
      </div>
      <p className="text-sm text-muted-foreground max-w-xs">{t('contractsList.desktopOnly')}</p>
    </div>
  );
}

// --- Main Page ---

function ContractsListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { status: searchStatus } = Route.useSearch();
  const { data: permissions } = usePermissions();
  const isLawyer = permissions?.role === 'LAWYER';

  // Filters state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchStatus ?? '');
  const [typeFilter, setTypeFilter] = useState('');
  const [createdByFilter, setCreatedByFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page on filter change
  const handleStatusChange = useCallback((v: string) => {
    setStatusFilter(v);
    setPage(1);
  }, []);

  const handleTypeChange = useCallback((v: string) => {
    setTypeFilter(v);
    setPage(1);
  }, []);

  const handleCreatedByChange = useCallback((v: string) => {
    setCreatedByFilter(v);
    setPage(1);
  }, []);

  const handlePageSizeChange = useCallback((s: number) => {
    setPageSize(s);
    setPage(1);
  }, []);

  // Query
  const { data, isPending, isError } = useContracts({
    status: statusFilter || undefined,
    contractType: typeFilter || undefined,
    search: debouncedSearch || undefined,
    createdBy: createdByFilter || undefined,
    page,
    pageSize,
  });

  const contracts = data?.data ?? [];
  const pagination = data?.pagination ?? { page: 1, pageSize: 10, total: 0, totalPages: 0 };

  // Team members for "Created by" filter (OWNER/ADMIN only)
  const canSeeAllContracts = permissions?.role === 'OWNER' || permissions?.role === 'ADMIN';
  const { data: membersData } = useTeamMembers(
    { status: 'active', pageSize: 100 },
    { enabled: canSeeAllContracts },
  );
  const memberOptions = canSeeAllContracts
    ? (membersData?.data ?? [])
        .filter((m) => m.role !== 'LAWYER')
        .map((m) => ({
          value: m.userId,
          label: m.name || m.email,
        }))
    : [];

  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [cancelContract, setCancelContract] = useState<ContractListItem | null>(null);
  const [deleteContract, setDeleteContract] = useState<ContractListItem | null>(null);
  const [duplicateContract, setDuplicateContract] = useState<ContractListItem | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Duplicate handler — called from modal confirmation
  const queryClient = useQueryClient();
  const handleDuplicateConfirm = useCallback(async () => {
    if (!duplicateContract) return;
    setIsDuplicating(true);
    try {
      const response = await api.post<{
        data: { id: string; contractType: string; status: string };
      }>(`/contracts/${duplicateContract.id}/duplicate`);
      toast.success(t('contractsList.duplicateSuccess'));
      void queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      setDuplicateContract(null);

      const { id: newId, status } = response.data;

      if (status === 'DRAFT') {
        void navigate({ to: '/contracts/$contractId', params: { contractId: newId } });
      } else if (duplicateContract.contractType === 'NDA') {
        void navigate({ to: '/contracts/new-nda', search: { id: newId } });
      } else {
        void navigate({ to: '/contracts/new-b2b', search: { id: newId } });
      }
    } catch {
      toast.error(t('contractsList.duplicateError'));
    } finally {
      setIsDuplicating(false);
    }
  }, [duplicateContract, t, queryClient, navigate]);

  const handleOpen = useCallback(
    (id: string) => {
      // For FORM_FILLING contracts, navigate directly to the wizard
      const contract = contracts.find((c) => c.id === id);
      if (contract?.status === 'FORM_FILLING') {
        if (contract.contractType === 'NDA') {
          void navigate({ to: '/contracts/new-nda', search: { id } });
        } else {
          void navigate({ to: '/contracts/new-b2b', search: { id } });
        }
        return;
      }
      void navigate({ to: '/contracts/$contractId', params: { contractId: id } });
    },
    [navigate, contracts],
  );

  if (isPending) {
    return (
      <>
        <MobileMessage />
        <PageSkeleton />
      </>
    );
  }

  const hasFilters = !!debouncedSearch || !!statusFilter || !!typeFilter || !!createdByFilter;
  const isEmpty = contracts.length === 0 && !hasFilters;
  const isFiltered = contracts.length === 0 && hasFilters;

  return (
    <>
      {/* Mobile — show message instead of table */}
      <MobileMessage />

      {/* Desktop content */}
      <div className="mx-auto hidden max-w-5xl lg:block">
        {/* Sticky header + filters */}
        <div className="sticky top-0 z-10 -mt-6 space-y-6 bg-background pt-6 pb-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold tracking-tight">{t('contracts.title')}</h1>
            {!isLawyer && !isEmpty && (
              <Button className="cursor-pointer" onClick={() => setCreateOpen(true)}>
                <Plus className="size-4" />
                {t('contracts.createNew')}
              </Button>
            )}
          </div>

          {isEmpty ? null : (
            <Filters
              search={search}
              onSearchChange={setSearch}
              status={statusFilter}
              onStatusChange={handleStatusChange}
              contractType={typeFilter}
              onContractTypeChange={handleTypeChange}
              createdBy={createdByFilter}
              onCreatedByChange={handleCreatedByChange}
              memberOptions={memberOptions}
            />
          )}
        </div>

        {isEmpty ? (
          <EmptyState onCreateClick={() => setCreateOpen(true)} isLawyer={isLawyer} />
        ) : (
          <>
            {/* Table */}
            <Card>
              <CardContent>
                {isError ? (
                  <div className="py-8 text-center text-sm text-destructive">
                    {t('contractsList.loadError')}
                  </div>
                ) : isFiltered ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="flex size-12 items-center justify-center rounded-full bg-muted mb-4">
                      <Search className="size-6 text-muted-foreground" />
                    </div>
                    <h3 className="text-sm font-semibold">{t('contractsList.noResultsTitle')}</h3>
                    <p className="mt-1 text-sm text-muted-foreground max-w-xs">
                      {t('contractsList.noResultsDescription')}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="pb-3 pr-4 font-medium">
                            {t('contractsList.columnTitle')}
                          </th>
                          <th className="pb-3 pr-4 font-medium">{t('contractsList.columnType')}</th>
                          <th className="pb-3 pr-4 font-medium">
                            {t('contractsList.columnStatus')}
                          </th>
                          <th className="pb-3 pr-4 font-medium">
                            {t('contractsList.columnAuthor')}
                          </th>
                          <th className="pb-3 pr-4 font-medium">
                            {t('contractsList.columnCreated')}
                          </th>
                          <th className="pb-3 font-medium">{t('contractsList.columnActions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contracts.map((contract) => (
                          <ContractRow
                            key={contract.id}
                            contract={contract}
                            language={i18n.language}
                            onOpen={handleOpen}
                            onDuplicate={setDuplicateContract}
                            onCancel={setCancelContract}
                            onDelete={setDeleteContract}
                            isLawyer={isLawyer}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {pagination.total > 0 && (
                  <Pagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    pageSize={pageSize}
                    total={pagination.total}
                    onPageChange={setPage}
                    onPageSizeChange={handlePageSizeChange}
                  />
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Modals */}
      <CreateContractDialog open={createOpen} onOpenChange={setCreateOpen} />
      <CancelContractModal
        open={!!cancelContract}
        onOpenChange={(open) => !open && setCancelContract(null)}
        contract={cancelContract}
      />
      <DeleteContractModal
        open={!!deleteContract}
        onOpenChange={(open) => !open && setDeleteContract(null)}
        contract={deleteContract}
      />
      <DuplicateContractModal
        open={!!duplicateContract}
        onOpenChange={(open) => !open && setDuplicateContract(null)}
        contract={duplicateContract}
        onConfirm={handleDuplicateConfirm}
        isPending={isDuplicating}
      />
    </>
  );
}
